import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ActivatedRoute } from '@angular/router';
import { ESignService } from 'src/app/services/e-sign.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { UrlValidatorService } from 'src/app/services/url-validator.service';
import { DomainType } from 'src/app/enum/domain-type.enum';
import { AADHAAR_RESPONSE_CONSTANTS } from 'src/app/models/aadhaar-response.constants';
@Component({
  selector: 'app-aadhaar-response',
  templateUrl: './aadhaar-response.component.html',
  styleUrls: ['./aadhaar-response.component.scss'],
  standalone: false,
})
export class AadhaarResponseComponent implements OnInit {
  txn: any;
  reference: any;
  queryParams: any;
  aadhaarDetails: any;
  base64PdfData: string;
  redirectUrl: string;
  isEmailButtonDisabled: boolean = false;
  emailCooldownTime: number = 0;
  private emailCooldownInterval: any;
  signerData: any = [];
  signatoryDetails: any = {};

  // HASH mode: Doqfy never had the document, so there is nothing to email, print or download.
  get isHashSign(): boolean {
    return this.signerData?.sign_type === 'HASH';
  }

  constructor(
    private spinner: NgxSpinnerService,
    private sanitizer: DomSanitizer,
    private activeRouter: ActivatedRoute,
    private eSignService: ESignService,
    private messageService: MessageService,
    private urlValidator: UrlValidatorService,
  ) {
    this.activeRouter.queryParams.subscribe((queryParams) => {
      this.queryParams = queryParams;
      this.txn = queryParams.txn;
      this.reference = queryParams.reference;
      if (this.txn) {
        this.getAadhaarRes();
      } else {
        this.spinner.hide();
        this.aadhaarDetails = [];
      }
    });
  }

  ngOnInit(): void {
    if (window.self !== window.top) {
      window.top.location.href = window.self.location.href;
      return;
    }

    const isRedirectFlow =
      this.queryParams[AADHAAR_RESPONSE_CONSTANTS.QUERY_PARAM_REDIRECT] ===
        AADHAAR_RESPONSE_CONSTANTS.QUERY_PARAM_TRUE &&
      this.queryParams.signatory_id &&
      this.queryParams.order_id;

    if (isRedirectFlow) {
      this.eSignService
        .getExternalSignLink(
          this.queryParams.signatory_id,
          this.queryParams.order_id,
        )
        .subscribe(
          (res) => {
            if (
              res['message'] &&
              res['message'].includes('Image not captured')
            ) {
              this.messageService.add({
                severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_ERROR,
                detail: res['message'],
                life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_SHORT,
              });
              setTimeout(() => {
                window.location.reload();
              }, 5000);
              return;
            }

            this.redirectUrl = res['content']?.redirect_url;
            if (this.redirectUrl) {
              if (
                this.urlValidator.isValidRedirectUrl(
                  this.redirectUrl,
                  DomainType.EMUDHRA,
                )
              ) {
                setTimeout(() => {
                  if (
                    this.urlValidator.isValidRedirectUrl(
                      this.redirectUrl,
                      DomainType.EMUDHRA,
                    )
                  ) {
                    window.location.href = this.redirectUrl;
                  }
                }, AADHAAR_RESPONSE_CONSTANTS.REDIRECT_DELAY);
              } else {
                this.messageService.add({
                  severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_ERROR,
                  detail: AADHAAR_RESPONSE_CONSTANTS.MSG_INVALID_REDIRECT,
                  life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_SHORT,
                });
                this.handleFallback();
              }
            }
          },
          (error) => {
            console.error(
              AADHAAR_RESPONSE_CONSTANTS.ERROR_EXTERNAL_SIGN_LINK,
              error,
            );
            const errMsg = error?.error?.message || '';
            if (errMsg.includes('Image not captured')) {
              this.messageService.add({
                severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_ERROR,
                detail: errMsg,
                life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_SHORT,
              });
              setTimeout(() => {
                window.location.reload();
              }, 5000);
              return;
            }
            this.redirectUrl = null;
            this.handleFallback();
          },
        );
    }
    // When not in the eMudhra redirect flow (e.g. opened via email link),
    // do not close the window. The confirmation page should stay open.
    this.getSignerDetails();
  }

  getAadhaarRes() {
    this.spinner.show();
    setTimeout(() => {
      this.eSignService.getAadhaarDetails(this.queryParams).subscribe(
        (res) => {
          this.aadhaarDetails = res['content'];
          this.base64PdfData = this.aadhaarDetails?.response?.signedpdf;
          this.spinner.hide();
          // Only fall back (close) inside the eMudhra redirect flow when the
          // redirect could not be resolved. For the email/confirmation-view
          // flow, keep the page open so the user can see the confirmation.
          const isRedirectFlow =
            this.queryParams[
              AADHAAR_RESPONSE_CONSTANTS.QUERY_PARAM_REDIRECT
            ] === AADHAAR_RESPONSE_CONSTANTS.QUERY_PARAM_TRUE &&
            this.queryParams.signatory_id &&
            this.queryParams.order_id;
          if (isRedirectFlow && !this.redirectUrl) {
            this.handleFallback();
          }
        },
        (error) => {
          console.error(
            AADHAAR_RESPONSE_CONSTANTS.ERROR_AADHAAR_DETAILS,
            error,
          );
          this.spinner.hide();
          this.aadhaarDetails = [];
        },
      );
    }, AADHAAR_RESPONSE_CONSTANTS.SPINNER_DELAY);
  }

  private isValidBase64Pdf(base64String: string): boolean {
    if (!base64String || typeof base64String !== 'string') {
      return false;
    }

    const cleaned = base64String.replace(/\s/g, '');

    if (!AADHAAR_RESPONSE_CONSTANTS.BASE64_PATTERN.test(cleaned)) {
      return false;
    }

    try {
      const decoded = atob(
        cleaned.substring(
          0,
          Math.min(
            AADHAAR_RESPONSE_CONSTANTS.PDF_VALIDATION_LENGTH,
            cleaned.length,
          ),
        ),
      );
      return decoded.startsWith(AADHAAR_RESPONSE_CONSTANTS.PDF_HEADER);
    } catch {
      return false;
    }
  }

  getPdfUrl(): SafeResourceUrl | null {
    if (!this.isValidBase64Pdf(this.base64PdfData)) {
      this.messageService.add({
        severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_ERROR,
        detail: AADHAAR_RESPONSE_CONSTANTS.MSG_INVALID_DOCUMENT,
        life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_SHORT,
      });
      return null;
    }

    const dataUrl =
      AADHAAR_RESPONSE_CONSTANTS.DATA_URL_PDF_PREFIX + this.base64PdfData;
    if (this.urlValidator.isValidRedirectUrl(dataUrl, DomainType.EMUDHRA)) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
    } else {
      return null;
    }
  }

  private handleFallback() {
    window.close();
  }

  getSignerDetails() {
    this.eSignService.getSignerDetails(this.queryParams).subscribe((res) => {
      this.signerData = res['content'];
    });
    this.spinner.hide();
  }

  shareToEmail() {
    if (this.isEmailButtonDisabled) {
      this.messageService.add({
        severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_WARN,
        detail: `${AADHAAR_RESPONSE_CONSTANTS.MSG_EMAIL_COOLDOWN} ${this.emailCooldownTime} ${AADHAAR_RESPONSE_CONSTANTS.MSG_EMAIL_COOLDOWN_SUFFIX}`,
        life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_SHORT,
      });
      return;
    }

    const payload = {
      esign_id: this.queryParams.esign_id,
      signatory_id: this.queryParams.signatory_id,
      notify_type: AADHAAR_RESPONSE_CONSTANTS.NOTIFY_TYPE,
    };

    this.isEmailButtonDisabled = true;

    this.eSignService.sendEmailNotification(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_SUCCESS,
          detail: AADHAAR_RESPONSE_CONSTANTS.MSG_EMAIL_SUCCESS,
          life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_LONG,
        });
        this.startEmailCooldown();
      },
      error: (error) => {
        this.messageService.add({
          severity: AADHAAR_RESPONSE_CONSTANTS.SEVERITY_ERROR,
          detail: AADHAAR_RESPONSE_CONSTANTS.MSG_EMAIL_FAILED,
          life: AADHAAR_RESPONSE_CONSTANTS.MSG_LIFE_LONG,
        });
        this.isEmailButtonDisabled = false;
      },
    });
  }

  startEmailCooldown() {
    this.emailCooldownTime = AADHAAR_RESPONSE_CONSTANTS.EMAIL_COOLDOWN_SECONDS;

    this.emailCooldownInterval = setInterval(() => {
      this.emailCooldownTime--;

      if (this.emailCooldownTime <= 0) {
        this.isEmailButtonDisabled = false;
        clearInterval(this.emailCooldownInterval);
      }
    }, AADHAAR_RESPONSE_CONSTANTS.COOLDOWN_INTERVAL);
  }

  printDocument() {
    const documentBase64 = this.signerData?.document;
    if (documentBase64) {
      try {
        const byteArray = new Uint8Array(
          atob(documentBase64)
            .split('')
            .map((char) => char.charCodeAt(0)),
        );
        const file = new Blob([byteArray], {
          type: AADHAAR_RESPONSE_CONSTANTS.MIME_TYPE_PDF,
        });
        const fileURL = URL.createObjectURL(file);

        const iframe = document.createElement('iframe');
        iframe.style.position = AADHAAR_RESPONSE_CONSTANTS.IFRAME_POSITION;
        iframe.style.right = AADHAAR_RESPONSE_CONSTANTS.IFRAME_RIGHT;
        iframe.style.bottom = AADHAAR_RESPONSE_CONSTANTS.IFRAME_BOTTOM;
        iframe.style.width = AADHAAR_RESPONSE_CONSTANTS.IFRAME_WIDTH;
        iframe.style.height = AADHAAR_RESPONSE_CONSTANTS.IFRAME_HEIGHT;
        iframe.style.border = AADHAAR_RESPONSE_CONSTANTS.IFRAME_BORDER;
        iframe.style.visibility = AADHAAR_RESPONSE_CONSTANTS.IFRAME_VISIBILITY;

        document.body.appendChild(iframe);
        iframe.src = fileURL;

        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();

              const cleanupHandler = () => {
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                  }
                  URL.revokeObjectURL(fileURL);
                }, AADHAAR_RESPONSE_CONSTANTS.CLEANUP_DELAY);
              };

              iframe.contentWindow?.addEventListener(
                AADHAAR_RESPONSE_CONSTANTS.EVENT_AFTERPRINT,
                cleanupHandler,
              );

              setTimeout(
                cleanupHandler,
                AADHAAR_RESPONSE_CONSTANTS.FALLBACK_CLEANUP_DELAY,
              );
            } catch (e) {
              console.error(AADHAAR_RESPONSE_CONSTANTS.ERROR_PRINT, e);
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
              URL.revokeObjectURL(fileURL);
            }
          }, AADHAAR_RESPONSE_CONSTANTS.PRINT_LOAD_DELAY);
        };
      } catch (error) {}
    }
  }

  downloadDocument() {
    const documentBase64 = this.signerData?.document;
    const esignId = this.signatoryDetails?.esign_id;

    if (documentBase64) {
      try {
        const byteArray = new Uint8Array(
          atob(documentBase64)
            .split('')
            .map((char) => char.charCodeAt(0)),
        );
        const file = new Blob([byteArray], {
          type: AADHAAR_RESPONSE_CONSTANTS.MIME_TYPE_PDF,
        });
        const fileURL = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = fileURL;

        const sanitizedId = esignId
          ? esignId.replace(/ /g, AADHAAR_RESPONSE_CONSTANTS.SPACE_REPLACEMENT)
          : `${AADHAAR_RESPONSE_CONSTANTS.FILE_NAME_PREFIX}${Date.now()}`;

        const filename = `${sanitizedId}${AADHAAR_RESPONSE_CONSTANTS.FILE_EXTENSION}`;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.error(AADHAAR_RESPONSE_CONSTANTS.ERROR_DOWNLOAD, error);
      }
    }
  }
}
